import PPUtils from "../../utils/PPUtils"

const ruleengine_base_url = import.meta.env.VITE_RULEENGINE_BASE_URL || "http://localhost:8082/RuleEngine/backend";

function resolveCustomerId(explicit) {
     if (explicit !== undefined) {
          if (!PPUtils.isValidCustomerId(explicit)) {
               return null
          }
          return explicit
     }
     return PPUtils.getEffectiveCustomerId()
}

function urlHasUnresolvedPlaceholder(url) {
     return /customerId=(undefined|null)(?=&|$)/.test(url)
          || /\/(undefined|null)(?=\/|\?|$)/.test(url)
}

function buildRuleEngineUrl(url) {
     const customerId = PPUtils.getEffectiveCustomerId()
     if (!PPUtils.isValidCustomerId(customerId)) {
          return null
     }
     const urlString = String(url)
     if (urlHasUnresolvedPlaceholder(urlString)) {
          return null
     }
     return ruleengine_base_url + urlString
}

async function makeBackendRequest(url, options) {
     const fetchUrl = buildRuleEngineUrl(url);
     if (!fetchUrl) {
          return null;
     }
     try {
          const response = await fetch(fetchUrl, options);
          if (response.ok) {
               return response;
          } else {
               console.error(`Request failed: ${response.status} ${response.statusText} for ${fetchUrl}`);
               return null;
          }
     } catch (error) {
          console.error(`Network error for ${fetchUrl}:`, error);
          return null;
     }
}

async function makeBackendMutation(url, options) {
     const fetchUrl = buildRuleEngineUrl(url)
     if (!fetchUrl) {
          throw new Error("Kein Kunde gewählt")
     }
     let response
     try {
          response = await fetch(fetchUrl, options)
     } catch (error) {
          throw new Error(`Network request failed for ${url}: ${error.message}`)
     }
     if (!response.ok) {
          const details = (await response.text()).trim()
          throw new Error(`${response.status} ${response.statusText}${details ? `: ${details}` : ""}`)
     }
     return response
}

async function getCustomerName(customerId, token) {
     const response = await makeBackendRequest(`/api/customers/${customerId}/name`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          return await response.text();
     }
     return null;
}

async function getAllChannels(token) {
     const response = await makeBackendRequest(`/api/channels`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          return await response.json();
     }
     return null;
}

async function getAllRules(customerId, token) {
     const response = await makeBackendRequest(`/api/rules/undeletedRulesOrderedByPriorityAndChannelExceptBackupRules?customerId=${customerId}`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          return await response.json();
     }
     return null;
}

async function getRulesByKeyword(customerId, token, keyword) {
     const encoded = encodeURIComponent(keyword || "")
     const response = await makeBackendRequest(`/api/rules/by/keyword/${encoded}?customerId=${customerId}`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          return await response.json();
     }
     return null;
}

async function reloadAllPrices(customerId, token) {
     const response = await makeBackendRequest(`/api/backendEvents/resetAll?customerId=${customerId}`, {
          method: 'PUT',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });
     return response;
}

async function deleteAllPrices(customerId, token) {
     const response = await makeBackendRequest(`/api/calculatedPrices?customerId=${customerId}`, {
          method: 'DELETE',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });
     return response;
}

async function deleteRule(userId, token, ruleId) {
     const response = await makeBackendRequest(`/api/rules/${ruleId}?userId=${userId}`, {
          method: 'DELETE',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });
     return response;
}

async function activateRule(userId, token, ruleId) {
     const response = await makeBackendMutation(`/api/rules/${ruleId}/activate`, {
          method: "PUT",
          headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId }),
     })
     return response
}

async function deactivateRule(userId, token, ruleId) {
     const response = await makeBackendMutation(`/api/rules/${ruleId}/deactivate`, {
          method: "PUT",
          headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId }),
     })
     return response
}

async function triggerRule(token, ruleId) {
     await makeBackendMutation(`/api/rules/${ruleId}/execute`, {
          method: "PUT",
          headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
          },
          body: "{}",
     })
}

async function getCalculatedPrices(customerId, token, pageNumber, pageSize, filters = {}, sortField = null, sortOrder = 'asc') {
     let url = `/api/calculatedPrices?customerId=${customerId}&_pageNumber=${pageNumber}&_pageSize=${pageSize}`;

     Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
               url += `&${key}=${encodeURIComponent(value)}`;
          }
     });

     if (sortField) {
          url += `&_orderBy:${sortOrder}=${encodeURIComponent(sortField)}`;
     }

     const response = await makeBackendRequest(url, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          return await response.json();
     }
     return null;
}

async function getCalculatedPricesCount(customerId, token, filters = {}) {
     let url = `/api/calculatedPrices/count?customerId=${customerId}`;

     const params = Object.entries(filters)
          .filter(([, value]) => value !== undefined && value !== null && value !== '')
          .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
          .join('&');

     if (params) {
          url += `&${params}`;
     }

     const response = await makeBackendRequest(url, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          return await response.json();
     }
     return null;
}

async function downloadCsvResponse(response, filename) {
     const blob = await response.blob()
     const url = window.URL.createObjectURL(blob)
     const link = document.createElement("a")
     link.href = url
     link.setAttribute("download", filename)
     document.body.appendChild(link)
     link.click()
     link.remove()
     window.URL.revokeObjectURL(url)
     return true
}

async function requestCsvExport(url, token, filename) {
     const fetchUrl = buildRuleEngineUrl(url)
     if (!fetchUrl) {
          return {
               success: false,
               error: "Kein Kunde gewählt",
          }
     }
     try {
          const response = await fetch(fetchUrl, {
               method: "GET",
               headers: {
                    Authorization: `Bearer ${token}`,
               },
          })
          if (response.ok) {
               await downloadCsvResponse(response, filename)
               return { success: true }
          }
          const message = (await response.text()).trim()
          return {
               success: false,
               error: message || `Export fehlgeschlagen (${response.status})`,
          }
     } catch (error) {
          console.error(`CSV export network error for ${fetchUrl}:`, error)
          return {
               success: false,
               error: error?.message || "Netzwerkfehler beim Export",
          }
     }
}

async function getCalculatedPricesAsCSV(customerId, token, filters = {}) {
     let url = `/api/calculatedPrices/csv/${customerId}`
     const q = checklistFiltersToQuery(filters)
     if (q) {
          url += `?${q}`
     }
     return requestCsvExport(url, token, "prices.csv")
}

function checklistFiltersToQuery(filters = {}) {
     return Object.entries(filters)
          .filter(([, value]) => value !== undefined && value !== null && value !== '')
          .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
          .join('&')
}

async function getChecklistAsCSV(customerId, token, filters = {}) {
     let url = `/api/checklist/csv/${customerId}`
     const q = checklistFiltersToQuery(filters)
     if (q) {
          url += `?${q}`
     }
     return requestCsvExport(url, token, "checklist.csv")
}

async function getRefusedPricesAsCSV(customerId, token) {
     return streamCsvExport(
          `/api/refusedPrices/csv/${customerId}?customerId=${encodeURIComponent(customerId)}`,
          token,
          "refused-prices.csv"
     )
}

async function streamCsvExport(url, token, filename) {
     const fetchUrl = buildRuleEngineUrl(url)
     if (!fetchUrl) {
          return {
               success: false,
               error: "Kein Kunde gewählt",
          }
     }
     let fileHandle = null
     if (typeof window.showSaveFilePicker === "function") {
          try {
               fileHandle = await window.showSaveFilePicker({
                    suggestedName: filename,
                    types: [{
                         description: "CSV",
                         accept: { "text/csv": [".csv"] },
                    }],
               })
          } catch (error) {
               if (error?.name === "AbortError") {
                    return { cancelled: true }
               }
          }
     }
     try {
          const response = await fetch(fetchUrl, {
               method: "GET",
               headers: {
                    Authorization: `Bearer ${token}`,
               },
          })
          if (!response.ok) {
               const message = (await response.text()).trim()
               return {
                    success: false,
                    error: message || `Export fehlgeschlagen (${response.status})`,
               }
          }
          if (fileHandle && response.body) {
               const writable = await fileHandle.createWritable()
               await response.body.pipeTo(writable)
               return { success: true }
          }
          await downloadCsvResponse(response, filename)
          return { success: true }
     } catch (error) {
          console.error(`CSV export network error for ${fetchUrl}:`, error)
          return {
               success: false,
               error: error?.message || "Netzwerkfehler beim Export",
          }
     }
}

async function postChecklistAccept(token, entityIds, userId) {
     return await makeBackendMutation(`/api/checklist/accept`, {
          method: "POST",
          headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ checklistItemsIds: entityIds, userId }),
     })
}

async function postChecklistReject(token, entityIds, userId) {
     return await makeBackendMutation(`/api/tasks`, {
          method: "POST",
          headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
               type: "DELETE_SELECTED_CHECKLIST_ITEMS",
               updateChecklistTaskData: {
                    idList: entityIds,
                    userId,
               },
          }),
     })
}

async function postChecklistAdaptAndSave(token, entityIds, userId, isNettoFactor, factor, price) {
     return await makeBackendMutation(`/api/checklist/accept`, {
          method: "POST",
          headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
               checklistItemsIds: entityIds,
               userId,
               isNettoFactor,
               factor,
               price,
               changeItemsByFactor: factor != null,
          }),
     })
}

async function postChecklistAcceptSingle(token, item, userId) {
     return await makeBackendMutation(`/api/checklist/accept/single/byUser/${userId}`, {
          method: 'POST',
          headers: {
               'Content-Type': 'application/json',
               Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(item),
     })
}

async function putChecklistItem(token, item) {
     return await makeBackendRequest(`/api/checklist/${item.id}`, {
          method: 'PUT',
          headers: {
               'Content-Type': 'application/json',
               Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ price: item.price }),
     })
}

async function getIsStagingActive(token) {
     const response = await makeBackendRequest(`/api/settings/staging/isEnabled`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          return await response.json();
     }
     return null;
}

async function setStagingModeActive(customerId, token, setTo) {
     const response = await makeBackendRequest(`/api/settings/staging/${setTo}?customerId=${customerId}`, {
          method: 'PUT',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });
     return response;
}

async function getFailedCalculations(customerId, token, channelId) {
     if (channelId < 0) {
          return null;
     }

     const response = await makeBackendRequest(`/api/failedPriceCalculations?customerId=${customerId}&channelId=${channelId}`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          return await response.json();
     }
     return null;
}

async function getExcludedArticles(customerId, token, pageNumber, pageSize, filters = {}, sortField = null, sortOrder = 'asc') {
     let url = `/api/products?customerId=${customerId}&_pageNumber=${pageNumber}&_pageSize=${pageSize}`;

     Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
               url += `&${key}=${encodeURIComponent(value)}`;
          }
     });

     if (sortField) {
          url += `&_orderBy:${sortOrder}=${encodeURIComponent(sortField)}`;
     }

     const response = await makeBackendRequest(url, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          return await response.json();
     }
     return null;
}

async function getExcludedArticlesCount(customerId, token, filters = {}) {
     let url = `/api/products/count?customerId=${customerId}`;

     const params = Object.entries(filters)
          .filter(([, value]) => value !== undefined && value !== null && value !== '')
          .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
          .join('&');

     if (params) {
          url += `&${params}`;
     }

     const response = await makeBackendRequest(url, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          return await response.json();
     }
     return null;
}

async function updateArticle(token, article) {
     const response = await makeBackendRequest(`/api/products/${article.id}`, {
          method: 'PUT',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(article)
     });
     return response;
}

async function updateArticlesBulk(token, idToArticle) {
     const response = await makeBackendRequest(`/api/products`, {
          method: 'PUT',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(idToArticle)
     });
     return response;
}

async function excludeArticles(token, customerId, change) {
     const response = await makeBackendMutation(`/api/products/exclusions/exclude?customerId=${encodeURIComponent(customerId)}`, {
          method: "POST",
          headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(change),
     });
     return response.json();
}

async function removeArticleExclusions(token, customerId, change) {
     const response = await makeBackendMutation(`/api/products/exclusions/remove?customerId=${encodeURIComponent(customerId)}`, {
          method: "POST",
          headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(change),
     });
     return response.json();
}

async function getCurrentArticleExclusions(token, customerId, productIds) {
     if (!Array.isArray(productIds) || productIds.length === 0) {
          return [];
     }
     const response = await makeBackendRequest(
          `/api/products/exclusions/current?customerId=${encodeURIComponent(customerId)}&productIds=${encodeURIComponent(productIds.join(","))}`,
          {
               method: "GET",
               headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
               },
          }
     );
     return response ? response.json() : null;
}

async function getChecklistItems(token, pageNumber, pageSize, filters = {}, sortField = null, sortOrder = 'asc') {
     let url = `/api/checklist?_pageNumber=${pageNumber}&_pageSize=${pageSize}`;

     Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
               url += `&${key}=${encodeURIComponent(value)}`;
          }
     });

     if (sortField) {
          url += `&_orderBy:${sortOrder}=${encodeURIComponent(sortField)}`;
     }

     const response = await makeBackendRequest(url, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          return await response.json();
     }
     return null;
}

async function getChecklistItemsCount(token, filters = {}) {
     let url = `/api/checklist/count`;
     const params = Object.entries(filters)
          .filter(([, value]) => value !== undefined && value !== null && value !== '')
          .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
          .join('&');

     if (params) {
          url += `?${params}`;
     }

     const response = await makeBackendRequest(url, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          return await response.json();
     }
     return null;
}

async function getChecklistItemIds(token, filters = {}) {
     let url = `/api/checklist/ids`
     const q = checklistFiltersToQuery(filters)
     if (q) {
          url += `?${q}`
     }

     const response = await makeBackendRequest(url, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          return await response.json();
     }
     return null;
}

async function getRuleConstant(token, customerId, lang, channelId, globalConstant) {
     const response = await makeBackendRequest(`/api/ruleConstants/getConstant`, {
          method: 'POST',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
               customerId,
               lang,
               channelId,
               globalConstant
          })
     });

     if (response) {
          return await response.json();
     }
     return null;
}

async function updateRuleConstants(token, ruleState, channelId, customerId, channelObject = null) {
     const results = [];

     for (const [key, constantData] of Object.entries(ruleState)) {
          if (!constantData.changed) {
               continue;
          }

          let response;

          const rawValue = constantData.value !== null && constantData.value !== undefined
               ? constantData.value
               : constantData.defaultValue;
          let numericValue;
          if (typeof rawValue === 'boolean') {
               numericValue = rawValue ? 1 : 0;
          } else {
               numericValue = rawValue !== null && rawValue !== undefined && rawValue !== ''
                    ? parseFloat(rawValue)
                    : null;
          }

          if (constantData.id && constantData.id !== -1) {
               const updateBody = {
                    name: constantData.key,
                    value: numericValue,
                    customerId: customerId
               };

               if (channelObject) {
                    updateBody.channel = {
                         id: channelObject.id,
                         channelName: channelObject.channelName
                    };
               }

               response = await makeBackendRequest(`/api/ruleConstants/${constantData.id}`, {
                    method: 'PUT',
                    headers: {
                         "Content-Type": "application/json",
                         "Authorization": `Bearer ${token}`
                    },
                    body: JSON.stringify(updateBody)
               });
          } else {
               const createBody = {
                    name: constantData.key,
                    value: numericValue,
                    customerId: customerId
               };

               if (channelObject) {
                    createBody.channel = {
                         id: channelObject.id,
                         channelName: channelObject.channelName
                    };
               } else if (channelId) {
                    createBody.channel = { id: channelId };
               }

               response = await makeBackendRequest(`/api/ruleConstants`, {
                    method: 'POST',
                    headers: {
                         "Content-Type": "application/json",
                         "Authorization": `Bearer ${token}`
                    },
                    body: JSON.stringify(createBody)
               });
          }

          if (response) {
               let result = null;
               const text = await response.text();
               if (text) {
                    try { result = JSON.parse(text); } catch (e) { /* PUT returns empty body */ }
               }
               results.push({ key, success: true, data: result || { id: constantData.id } });
          } else {
               results.push({ key, success: false });
          }
     }

     const allSuccessful = results.every(r => r.success);
     return allSuccessful ? { ok: true, results } : null;
}

async function getLanguageList(token) {
     const response = await makeBackendRequest(`/api/products/languages`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          return await response.json();
     }
     return null;
}

async function createProviderFilter(token, providerFilter) {
     const response = await makeBackendRequest("/api/providerFilter", {
          method: 'POST',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(providerFilter)
     });
     return response;
}

async function getRuleGroups(token, customerId, channelId) {
     const response = await makeBackendRequest(`/api/ruleGroups?channel.id=${channelId}&customerId=${customerId}`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });
     if (response) {
          const data = await response.json();
          return data;
     }
     return null;
}

async function getLanguages(token) {
     const response = await makeBackendRequest(`/api/products/languages`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });
     if (response) {
          return await response.json();
     }
     return null;
}

async function getChannels(customerId, token) {
     let response = await makeBackendRequest(`/api/channels?customerId=${customerId}`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (!response) {
          response = await makeBackendRequest(`/api/channels/${customerId}`, {
               method: 'GET',
               headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
               },
          });
     }

     if (response) {
          return await response.json();
     }
     return null;
}

async function createChannel(token, customerId, channelName) {
     const response = await makeBackendRequest(`/api/channels/create`, {
          method: "POST",
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
               channelName,
               hidden: false,
               customerId
          })
     });

     if (response) {
          return await response.json();
     }
     return null;
}

async function activateChannel(token, customerId, channelId) {
     const response = await makeBackendRequest(`/api/channels/activate/${channelId}?customerId=${customerId}`, {
          method: "POST",
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          }
     });
     return response;
}

async function deactivateChannel(token, customerId, channelId) {
     const response = await makeBackendRequest(`/api/channels/deactivate/${channelId}?customerId=${customerId}`, {
          method: "POST",
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          }
     });
     return response;
}

async function getShops(token) {
     const response = await makeBackendRequest(`/api/shops`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });
     if (response) {
          return await response.json();
     }
     return null;
}

async function getShopsByLanguage(token, language) {
     const url = language ? `/api/shops?lang=${language}` : `/api/shops`;
     const response = await makeBackendRequest(url, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });
     if (response) {
          return await response.json();
     }
     return null;
}

async function getProductAttributeNumericKeys(token) {
     const response = await makeBackendRequest(`/api/productAttributes/numericKeys`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });
     if (response) {
          return await response.json();
     }
     return null;
}

async function getProductAttributeKeysWithType(token, customerId) {
     const response = await makeBackendRequest(`/api/productAttributes/keysWithType?customerId=${customerId}`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });
     if (response) {
          return await response.json();
     }
     return null;
}

async function getTopCategories(token, customerId) {
     const response = await makeBackendRequest(`/api/products/topCategories?customerId=${customerId}`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });
     if (response) {
          return await response.json();
     }
     return null;
}

async function getLevel2Categories(token, customerId, departments) {
     const response = await makeBackendRequest(`/api/products/level2Categories?customerId=${customerId}`, {
          method: 'POST',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
               departments: departments
          })
     });
     if (response) {
          return await response.json();
     }
     return null;
}

async function getLevel3Categories(token, customerId, productGroups) {
     const response = await makeBackendRequest(`/api/products/level3Categories?customerId=${customerId}`, {
          method: 'POST',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
               productGroups: productGroups
          })
     });
     if (response) {
          return await response.json();
     }
     return null;
}

async function getCategoryTree(token, customerId, lang, departments, productGroups, productCategories, producers) {
     const response = await makeBackendRequest(`/api/products/categoryTree?customerId=${customerId}&lang=${lang}`, {
          method: 'POST',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
               department: departments || [],
               product_group: productGroups || [],
               product_cat: productCategories || [""],
               product_producer: producers || []
          })
     });
     if (response) {
          return await response.json();
     }
     return null;
}

async function getRulesByChannel(channelId, customerId, token) {
     const response = await makeBackendRequest(`/api/rules?channel.id=${channelId}&customerId=${customerId}`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });
     if (response) {
          return await response.json();
     }
     return null;
}

async function createRuleGroup(token, customerId, channelOrId, groupName, channelName = "Onlineshop") {
     const channel = typeof channelOrId === "object" && channelOrId !== null
          ? {
               id: channelOrId.id,
               channelName: channelOrId.channelName || channelName,
               hidden: channelOrId.hidden ?? false
          }
          : {
               id: channelOrId,
               channelName,
               hidden: false
          };

     const requestBody = {
          id: null,
          groupName: groupName,
          channel,
          customerId: customerId
     };

     const response = await makeBackendRequest('/api/ruleGroups', {
          method: 'POST',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify(requestBody)
     });

     if (response) {
          const data = await response.json();
          return data;
     }
     return null;
}

async function createRule(token, ruleData) {
     const response = await makeBackendMutation(`/api/rules`, {
          method: 'PUT',
          headers: {
               'Content-Type': 'application/json',
               'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(ruleData)
     });
     return await response.json();
}

function normalizeDateForBackend(dateValue) {
     if (!dateValue) return null;
     let dateStr = String(dateValue);
     dateStr = dateStr.split('+')[0];
     dateStr = dateStr.split('Z')[0];
     dateStr = dateStr.split('.')[0];
     return dateStr;
}

async function updateRule(token, ruleData) {
     if (ruleData?.rule?.createDate) {
          ruleData.rule.createDate = normalizeDateForBackend(ruleData.rule.createDate);
     }
     const response = await makeBackendMutation(`/api/rules`, {
          method: 'POST',
          headers: {
               'Content-Type': 'application/json',
               'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(ruleData)
     });
     return await response.json();
}

async function getRuleById(token, ruleId) {
     const response = await makeBackendRequest(`/api/rules/${ruleId}`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          const rule = await response.json();
          return rule;
     }
     return null;
}

async function getBackupRules(token, customerId) {
     const response = await makeBackendRequest(`/api/rules/backupRules?customerId=${customerId}`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          const backupRules = await response.json();
          return backupRules;
     }
     return [];
}

async function getProductAttributeStringValues(token, customerId, attributeKey) {
     const response = await makeBackendRequest(`/api/productAttributes/stringValuesByKey?customerId=${customerId}`, {
          method: 'POST',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
               key: attributeKey
          })
     });
     if (response) {
          return await response.json();
     }
     return null;
}

async function getProducers(token, customerId) {
     const response = await makeBackendRequest(`/api/products/producers?customerId=${customerId}`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });
     if (response) {
          return await response.json();
     }
     return null;
}

async function getSecurityMonitoringEmails(token) {
     const response = await makeBackendRequest(`/api/settings/securityMonitoringEmails`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });

     if (response) {
          return await response.json();
     }
     return null;
}

async function setSecurityMonitoringEmails(token, emails) {
     const response = await makeBackendRequest(`/api/settings/securityMonitoringEmails`, {
          method: 'PUT',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ emails })
     });
     return response;
}

async function getProviderFilters(token, customerId) {
     const response = await makeBackendRequest(`/api/providerFilter?customerId=${customerId}`, {
          method: "GET",
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          }
     });

     if (response) {
          return await response.json();
     }
     return [];
}

async function updateProviderFilter(token, filterId, providerFilter) {
     const response = await makeBackendRequest(`/api/providerFilter/${filterId}`, {
          method: "PUT",
          headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(providerFilter),
     })
     return response
}

async function deleteProviderFilter(token, filterId) {
     const response = await makeBackendRequest(`/api/providerFilter/${filterId}`, {
          method: "DELETE",
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          }
     });
     return response;
}

async function getProductIdsByCategoryTree(token, customerId, restrictions) {
     const response = await makeBackendMutation(`/api/products/ids/categoryTree?customerId=${customerId}`, {
          method: "POST",
          headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(restrictions),
     });
     return await response.json();
}

async function findProductIdsByProductNrs(token, customerId, lang, productnrs) {
     const response = await makeBackendMutation(`/api/products/lang/${lang}/idsByProductnrs?customerId=${customerId}`, {
          method: "POST",
          headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ productnrs }),
     });
     return await response.json();
}

async function simulateRule(token, payload) {
     const customerId = resolveCustomerId(payload?.customerId)
     if (customerId == null) {
          throw new Error("Kein Kunde gewählt")
     }
     const fetchUrl = `${ruleengine_base_url}/api/rules/simulate?customerId=${encodeURIComponent(customerId)}`;
     let response;
     try {
          response = await fetch(fetchUrl, {
               method: "PUT",
               headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
               },
               body: JSON.stringify(payload),
          });
     } catch (error) {
          throw new Error(`Netzwerkfehler bei der Regelsimulation: ${error.message}`);
     }
     if (!response.ok) {
          const details = (await response.text()).trim();
          throw new Error(`${response.status} ${response.statusText}${details ? `: ${details}` : ""}`);
     }
     return await response.json();
}

async function getProductAttributeTextKeys(token) {
     const response = await makeBackendRequest(`/api/productAttributes/textKeys`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });
     if (response) {
          return await response.json();
     }
     return null;
}

async function getRuleHistory(token, ruleId) {
     const response = await makeBackendRequest(`/api/history?ruleId=${ruleId}&_orderBy:desc=createDate`, {
          method: 'GET',
          headers: {
               "Content-Type": "application/json",
               "Authorization": `Bearer ${token}`
          },
     });
     if (response) {
          return await response.json();
     }
     return null;
}

export default {
     getCustomerName,
     getAllChannels,
     getAllRules,
     getRulesByKeyword,
     reloadAllPrices,
     deleteAllPrices,
     getCalculatedPrices,
     getCalculatedPricesCount,
     deleteRule,
     activateRule,
     deactivateRule,
     triggerRule,
     getIsStagingActive,
     setStagingModeActive,
     getFailedCalculations,
     getExcludedArticles,
     getExcludedArticlesCount,
     excludeArticles,
     removeArticleExclusions,
     getCurrentArticleExclusions,
     getCalculatedPricesAsCSV,
     getChecklistAsCSV,
     getRefusedPricesAsCSV,
     postChecklistAccept,
     postChecklistReject,
     postChecklistAdaptAndSave,
     postChecklistAcceptSingle,
     putChecklistItem,
     createProviderFilter,
     updateProviderFilter,
     updateArticle,
     updateArticlesBulk,
     getChecklistItems,
     getChecklistItemsCount,
     getChecklistItemIds,
     getRuleConstant,
     updateRuleConstants,
     getLanguageList,
     getRuleGroups,
     createRuleGroup,
     getLanguages,
     getChannels,
     createChannel,
     activateChannel,
     deactivateChannel,
     getShops,
     getShopsByLanguage,
     getProductAttributeNumericKeys,
     getProductAttributeKeysWithType,
     getProductAttributeTextKeys,
     getTopCategories,
     getLevel2Categories,
     getLevel3Categories,
     getCategoryTree,
     getRulesByChannel,
     createRule,
     updateRule,
     getRuleById,
     getBackupRules,
     getProductAttributeStringValues,
     getProducers,
     getProviderFilters,
     deleteProviderFilter,
     getSecurityMonitoringEmails,
     setSecurityMonitoringEmails,
     getProductIdsByCategoryTree,
     findProductIdsByProductNrs,
     simulateRule,
     getRuleHistory,
     buildRuleEngineUrl,
     resolveCustomerId
}

export { buildRuleEngineUrl, resolveCustomerId }